import axios from 'axios'
import { getToken, getRefreshToken, setToken, setRefreshToken, clearAuth, isTokenExpiringSoon } from '@/utils/auth.js'
import { ElMessage } from 'element-plus'

// 使用相对路径，通过前端代理访问后端API
// 这样支持本地开发和外网访问
const apiClient = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json'
  }
})

// 是否正在刷新Token
let isRefreshing = false
// 等待刷新Token的请求队列
let refreshSubscribers = []

/**
 * 将请求加入队列
 */
function subscribeTokenRefresh(callback) {
  refreshSubscribers.push(callback)
}

/**
 * 通知所有等待的请求使用新Token
 */
function onTokenRefreshed(newToken) {
  refreshSubscribers.forEach(callback => callback(newToken))
  refreshSubscribers = []
}

/**
 * 刷新Token
 */
async function refreshAccessToken() {
  try {
    const refreshToken = getRefreshToken()
    if (!refreshToken) {
      throw new Error('No refresh token')
    }

    // 使用 axios 直接请求，避免拦截器循环
    const response = await axios.post('/api/auth/refresh', {
      refresh_token: refreshToken
    }, {
      baseURL: '/api',
      headers: {
        'Content-Type': 'application/json'
      }
    })

    if (response.data.success) {
      setToken(response.data.access_token)
      setRefreshToken(response.data.refresh_token)
      return response.data.access_token
    }
    throw new Error('Refresh token failed')
  } catch (error) {
    clearAuth()
    // 不在这里跳转，让调用者处理
    throw error
  }
}

// 请求拦截器
apiClient.interceptors.request.use(
  config => {
    const token = getToken()

    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// 响应拦截器
apiClient.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config
    
    // 如果是401错误且不是刷新Token的请求
    if (error.response?.status === 401 && !originalRequest.url.includes('/auth/refresh')) {
      // 如果正在刷新Token，将请求加入队列
      if (isRefreshing) {
        return new Promise(resolve => {
          subscribeTokenRefresh(newToken => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`
            resolve(apiClient(originalRequest))
          })
        })
      }
      
      isRefreshing = true
      
      try {
        const newToken = await refreshAccessToken()
        onTokenRefreshed(newToken)
        originalRequest.headers.Authorization = `Bearer ${newToken}`
        return apiClient(originalRequest)
      } catch (refreshError) {
        ElMessage.error('登录已过期，请重新登录')
        clearAuth()
        window.location.href = '/login'
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }
    
    // 处理403权限不足
    if (error.response?.status === 403) {
      ElMessage.error('权限不足，无法执行此操作')
    }
    
    return Promise.reject(error)
  }
)

// CMDB API
export const cmdbAPI = {
  getDevices: () => apiClient.get('/cmdb/devices'),
  getDevice: (id) => apiClient.get(`/cmdb/devices/${id}`),
  createDevice: (data) => apiClient.post('/cmdb/devices', data),
  updateDevice: (id, data) => apiClient.put(`/cmdb/devices/${id}`, data),
  deleteDevice: (id) => apiClient.delete(`/cmdb/devices/${id}`)
}

// 应用集 API
export const applicationsAPI = {
  getApplications: (params) => apiClient.get('/monitoring/applications', { params }),
  getApplication: (id) => apiClient.get(`/applications/applications/${id}`),
  getApplicationDetail: (id) => apiClient.get(`/applications/applications/${id}/detail`),
  createApplication: (data) => apiClient.post('/applications/applications', data),
  updateApplication: (id, data) => apiClient.put(`/applications/applications/${id}`, data),
  deleteApplication: (id) => apiClient.delete(`/applications/applications/${id}`),
  getApplicationItems: (id) => apiClient.get(`/applications/applications/${id}/items`),
  addItemToApplication: (appId, itemId) => apiClient.post(`/applications/applications/${appId}/items/${itemId}`),
  removeItemFromApplication: (appId, itemId) => apiClient.delete(`/applications/applications/${appId}/items/${itemId}`),
  getApplicationTriggers: (id) => apiClient.get(`/applications/applications/${id}/triggers`),
  addTriggerToApplication: (appId, triggerId) => apiClient.post(`/applications/applications/${appId}/triggers/${triggerId}`),
  removeTriggerFromApplication: (appId, triggerId) => apiClient.delete(`/applications/applications/${appId}/triggers/${triggerId}`),
  getApplicationGraphs: (id) => apiClient.get(`/applications/applications/${id}/graphs`),
  addGraphToApplication: (appId, graphId) => apiClient.post(`/applications/applications/${appId}/graphs/${graphId}`),
  removeGraphFromApplication: (appId, graphId) => apiClient.delete(`/applications/applications/${appId}/graphs/${graphId}`)
}

// OID模板 API
export const oidTemplatesAPI = {
  getTemplates: (params) => apiClient.get('/oid-templates/oid-templates', { params }),
  getTemplate: (id) => apiClient.get(`/oid-templates/oid-templates/${id}`),
  createTemplate: (data) => apiClient.post('/oid-templates/oid-templates', data),
  updateTemplate: (id, data) => apiClient.put(`/oid-templates/oid-templates/${id}`, data),
  deleteTemplate: (id) => apiClient.delete(`/oid-templates/oid-templates/${id}`),
  getCategories: () => apiClient.get('/oid-templates/oid-templates/categories/list')
}

// OID模板组 API
export const oidTemplateGroupsAPI = {
  getGroups: (params) => apiClient.get('/oid-template-groups', { params }),
  getGroup: (id) => apiClient.get(`/oid-template-groups/${id}`),
  createGroup: (data) => apiClient.post('/oid-template-groups', data),
  updateGroup: (id, data) => apiClient.put(`/oid-template-groups/${id}`, data),
  deleteGroup: (id) => apiClient.delete(`/oid-template-groups/${id}`),
  getCategories: () => apiClient.get('/oid-template-groups/categories/list'),
  addTemplateToGroup: (groupId, templateId) => apiClient.post(`/oid-template-groups/${groupId}/add-template`, null, { params: { template_id: templateId } }),
  removeTemplateFromGroup: (groupId, templateId) => apiClient.post(`/oid-template-groups/${groupId}/remove-template`, null, { params: { template_id: templateId } }),
  batchCreateFromGroup: (data) => apiClient.post('/monitoring/items/batch-create-from-template-group', data)
}

// 监控 API
export const monitoringAPI = {
  getItems: () => apiClient.get('/monitoring/items'),
  getItem: (id) => apiClient.get(`/monitoring/items/${id}`),
  createItem: (data) => apiClient.post('/monitoring/items', data),
  updateItem: (id, data) => apiClient.put(`/monitoring/items/${id}`, data),
  deleteItem: (id) => apiClient.delete(`/monitoring/items/${id}`),
  getItemLatestData: (id) => apiClient.get(`/monitoring/items/${id}/latest-data`),
  collectData: (id) => apiClient.post(`/monitoring/items/${id}/collect`),
  mockCollectData: (id) => apiClient.post(`/monitoring/items/${id}/mock-collect`),

  getTriggers: () => apiClient.get('/monitoring/triggers'),
  getTrigger: (id) => apiClient.get(`/monitoring/triggers/${id}`),
  createTrigger: (data) => apiClient.post('/monitoring/triggers', data),
  updateTrigger: (id, data) => apiClient.put(`/monitoring/triggers/${id}`, data),
  deleteTrigger: (id) => apiClient.delete(`/monitoring/triggers/${id}`),
  createWindowsCpuTriggers: (monitoringItemId) => apiClient.post(`/monitoring/triggers/create-windows-cpu/${monitoringItemId}`),
  createWindowsMemoryTriggers: (monitoringItemId) => apiClient.post(`/monitoring/triggers/create-windows-memory/${monitoringItemId}`),
  createMultiItemTriggers: (monitoringItemId) => apiClient.post(`/monitoring/triggers/create-multi-item/${monitoringItemId}`),
  evaluateTrigger: (id) => apiClient.post(`/monitoring/triggers/evaluate/${id}`),

  getData: (params) => apiClient.get('/monitoring/data', { params })
}

// 触发器 API (独立导出，方便使用)
export const triggersAPI = {
  getTriggers: () => apiClient.get('/monitoring/triggers'),
  getTrigger: (id) => apiClient.get(`/monitoring/triggers/${id}`),
  createTrigger: (data) => apiClient.post('/monitoring/triggers', data),
  updateTrigger: (id, data) => apiClient.put(`/monitoring/triggers/${id}`, data),
  deleteTrigger: (id) => apiClient.delete(`/monitoring/triggers/${id}`)
}

// SNMP API
export const snmpAPI = {
  getStatus: () => apiClient.get('/snmp/status'),
  getConfigs: () => apiClient.get('/snmp/configs'),
  getConfig: (id) => apiClient.get(`/snmp/configs/${id}`),
  createConfig: (data) => apiClient.post('/snmp/configs', data),
  updateConfig: (id, data) => apiClient.put(`/snmp/configs/${id}`, data),
  deleteConfig: (id) => apiClient.delete(`/snmp/configs/${id}`),
  
  getMIBs: () => apiClient.get('/snmp/mibs'),
  getMIB: (id) => apiClient.get(`/snmp/mibs/${id}`),
  createMIB: (data) => apiClient.post('/snmp/mibs', data),
  updateMIB: (id, data) => apiClient.put(`/snmp/mibs/${id}`, data),
  deleteMIB: (id) => apiClient.delete(`/snmp/mibs/${id}`),
  
  collectData: (deviceId) => apiClient.get(`/snmp/collect/${deviceId}`),
  
  // SNMP 测试和 WALK
  testOID: (data) => apiClient.post('/snmp/test', data),
  walkOID: (data) => apiClient.post('/snmp/walk', data, { timeout: 30000 }),
  
  // SNMP WALK
  walk: (data) => apiClient.post('/snmp/walk', data),
  getWalkResults: (deviceId) => apiClient.get(`/snmp/walk/results/${deviceId}`),
  clearWalkResults: (deviceId) => apiClient.delete(`/snmp/walk/results/${deviceId}`),
  
  // SNMP Test
  test: (data) => apiClient.post('/snmp/test', data),
  getTestRecords: (deviceId) => apiClient.get(`/snmp/test/records/${deviceId}`),
  clearTestRecords: (deviceId) => apiClient.delete(`/snmp/test/records/${deviceId}`)
}

// MIB文件 API
export const mibFilesAPI = {
  getFiles: () => apiClient.get('/mib-files/files'),
  getFile: (id) => apiClient.get(`/mib-files/files/${id}`),
  uploadFile: (formData) => apiClient.post('/mib-files/files/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateFile: (id, data) => apiClient.put(`/mib-files/files/${id}`, data),
  deleteFile: (id) => apiClient.delete(`/mib-files/files/${id}`),
  getTree: (fileId) => apiClient.get(`/mib-files/files/${fileId}/nodes/tree`),
  getNodes: (fileId, parentId) => apiClient.get(`/mib-files/files/${fileId}/nodes`, { params: { parent_id: parentId } }),
  searchNodes: (keyword) => apiClient.get('/mib-files/nodes/search', { params: { keyword } })
}

// 图形 API
export const graphsAPI = {
  getGraphs: (params) => apiClient.get('/graphs/graphs', { params }),
  getGraph: (id) => apiClient.get(`/graphs/graphs/${id}`),
  createGraph: (data) => apiClient.post('/graphs/graphs', data),
  updateGraph: (id, data) => apiClient.put(`/graphs/graphs/${id}`, data),
  deleteGraph: (id) => apiClient.delete(`/graphs/graphs/${id}`),
  getGraphData: (id, hours) => apiClient.get(`/graphs/graphs/${id}/data`, { params: { hours } }),
  addGraphItem: (graphId, data) => apiClient.post(`/graphs/graphs/${graphId}/items`, data),
  removeGraphItem: (graphId, itemId) => apiClient.delete(`/graphs/graphs/${graphId}/items/${itemId}`)
}

// 告警 API
export const alertsAPI = {
  getAlerts: (params) => apiClient.get('/alerts', { params }),
  getAlertsPaginated: (params) => apiClient.get('/alerts/paginated', { params }),
  getAlert: (id) => apiClient.get(`/alerts/${id}`),
  createAlert: (data) => apiClient.post('/alerts', data),
  updateAlert: (id, data) => apiClient.put(`/alerts/${id}`, data),
  deleteAlert: (id) => apiClient.delete(`/alerts/${id}`),
  acknowledgeAlert: (id) => apiClient.post(`/alerts/${id}/acknowledge`),
  getDeviceAlerts: (deviceId) => apiClient.get(`/alerts/device/${deviceId}`),
  getTriggerAlerts: (triggerId) => apiClient.get(`/alerts/trigger/${triggerId}`),
  // 批量操作
  batchDeleteAlerts: (ids) => apiClient.post('/alerts/batch-delete', { ids }),
  batchResolveAlerts: (ids) => apiClient.post('/alerts/batch-resolve', { ids }),
  batchAcknowledgeAlerts: (ids) => apiClient.post('/alerts/batch-acknowledge', { ids })
}

// 远程管理 API
export const remoteAPI = {
  getConnections: (params) => apiClient.get('/remote/connections', { params }),
  getConnection: (id) => apiClient.get(`/remote/connections/${id}`),
  createConnection: (data) => apiClient.post('/remote/connections', data),
  updateConnection: (id, data) => apiClient.put(`/remote/connections/${id}`, data),
  deleteConnection: (id) => apiClient.delete(`/remote/connections/${id}`),
  testConnection: (id) => apiClient.post(`/remote/connections/${id}/test`),
  batchTestConnections: (data) => apiClient.post('/remote/connections/batch-test', data),
  connect: (data) => apiClient.post('/remote/connect', data),
  batchConnect: (data) => apiClient.post('/remote/connections/batch-connect', data),
  disconnect: (sessionId) => apiClient.post(`/remote/sessions/${sessionId}/disconnect`),
  getSessions: (params) => apiClient.get('/remote/sessions', { params }),
  getConnectionSessions: (connectionId) => apiClient.get(`/remote/connections/${connectionId}/sessions`),
  getSessionLogs: (sessionId) => apiClient.get(`/remote/sessions/${sessionId}/logs`)
}

// 自动发现 API
export const discoveryAPI = {
  getTasks: (params) => apiClient.get('/discovery/tasks', { params }),
  getTask: (id) => apiClient.get(`/discovery/tasks/${id}`),
  createTask: (data) => apiClient.post('/discovery/tasks', data),
  updateTask: (id, data) => apiClient.put(`/discovery/tasks/${id}`, data),
  deleteTask: (id) => apiClient.delete(`/discovery/tasks/${id}`),
  executeTask: (data) => apiClient.post('/discovery/execute', data),
  
  getResults: (params) => apiClient.get('/discovery/results', { params }),
  importResults: (data) => apiClient.post('/discovery/import', data),
  ignoreResult: (id) => apiClient.post(`/discovery/results/${id}/ignore`),
  
  getRules: (params) => apiClient.get('/discovery/rules', { params }),
  createRule: (data) => apiClient.post('/discovery/rules', data),
  updateRule: (id, data) => apiClient.put(`/discovery/rules/${id}`, data),
  deleteRule: (id) => apiClient.delete(`/discovery/rules/${id}`)
}

// 定时任务 API
export const scheduledTasksAPI = {
  // 任务管理
  getTasks: (params) => apiClient.get('/scheduled-tasks/tasks', { params }),
  getTask: (id) => apiClient.get(`/scheduled-tasks/tasks/${id}`),
  createTask: (data) => apiClient.post('/scheduled-tasks/tasks', data),
  updateTask: (id, data) => apiClient.put(`/scheduled-tasks/tasks/${id}`, data),
  deleteTask: (id) => apiClient.delete(`/scheduled-tasks/tasks/${id}`),
  toggleTask: (id) => apiClient.post(`/scheduled-tasks/tasks/${id}/toggle`),
  
  // 任务执行
  executeTask: (data) => apiClient.post('/scheduled-tasks/execute', data),
  
  // 执行日志
  getLogs: (params) => apiClient.get('/scheduled-tasks/logs', { params }),
  getTaskLogs: (taskId) => apiClient.get(`/scheduled-tasks/tasks/${taskId}/logs`),
  deleteLog: (id) => apiClient.delete(`/scheduled-tasks/logs/${id}`),
  
  // AI辅助Cron生成
  generateCron: (data) => apiClient.post('/scheduled-tasks/ai-generate-cron', data),
  getAICronHistory: (limit = 20) => apiClient.get('/scheduled-tasks/ai-cron-history', { params: { limit } }),
  
  // Cron验证
  validateCron: (data) => apiClient.post('/scheduled-tasks/validate-cron', data),

  // 重试
  retryTask: (id) => apiClient.post(`/scheduled-tasks/tasks/${id}/retry`)
}

// AI工作流 API
export const aiWorkflowAPI = {
  getWorkflows: (params) => apiClient.get('/ai-workflow/workflows', { params }),
  getWorkflow: (id) => apiClient.get(`/ai-workflow/workflows/${id}`),
  getRun: (runId) => apiClient.get(`/ai-workflow/runs/${runId}`),
  listRuns: (params) => apiClient.get('/ai-workflow/runs', { params })
}

// AI助手 API
export const aiAPI = {
  // 平台配置
  getPlatforms: (params) => apiClient.get('/ai/platforms', { params }),
  getPlatform: (id) => apiClient.get(`/ai/platforms/${id}`),
  createPlatform: (data) => apiClient.post('/ai/platforms', data),
  updatePlatform: (id, data) => apiClient.put(`/ai/platforms/${id}`, data),
  deletePlatform: (id) => apiClient.delete(`/ai/platforms/${id}`),
  testPlatform: (id, testMessage = 'Hello, this is a test message.') => apiClient.post(`/ai/platforms/${id}/test`, { test_message: testMessage }),
  getPlatformTypes: () => apiClient.get('/ai/platforms/types'),
  getPlatformModels: (platformType) => apiClient.get('/ai/platforms/models', { params: { platform_type: platformType } }),
  
  // 聊天会话
  getSessions: (params) => apiClient.get('/ai/sessions', { params }),
  getSession: (id) => apiClient.get(`/ai/sessions/${id}`),
  createSession: (data) => apiClient.post('/ai/sessions', data),
  updateSession: (id, data) => apiClient.put(`/ai/sessions/${id}`, data),
  deleteSession: (id) => apiClient.delete(`/ai/sessions/${id}`),
  getSessionMessages: (sessionId) => apiClient.get(`/ai/sessions/${sessionId}/messages`),
  
  // 聊天
  chat: (data) => apiClient.post('/ai/chat', data),
  
  // 预设问题
  getPresetQuestions: (params) => apiClient.get('/ai/preset-questions', { params }),
  getPresetQuestion: (id) => apiClient.get(`/ai/preset-questions/${id}`),
  createPresetQuestion: (data) => apiClient.post('/ai/preset-questions', data),
  updatePresetQuestion: (id, data) => apiClient.put(`/ai/preset-questions/${id}`, data),
  deletePresetQuestion: (id) => apiClient.delete(`/ai/preset-questions/${id}`),
  
  // 分析任务
  getAnalysisTasks: (params) => apiClient.get('/ai/analysis-tasks', { params }),
  getAnalysisTask: (id) => apiClient.get(`/ai/analysis-tasks/${id}`),
  createAnalysisTask: (data) => apiClient.post('/ai/analysis-tasks', data),
  deleteAnalysisTask: (id) => apiClient.delete(`/ai/analysis-tasks/${id}`)
}

// 资产管理 API
export const assetAPI = {
  // 设备品牌
  getBrands: (params) => apiClient.get('/assets/brands', { params }),
  getBrand: (id) => apiClient.get(`/assets/brands/${id}`),
  createBrand: (data) => apiClient.post('/assets/brands', data),
  updateBrand: (id, data) => apiClient.put(`/assets/brands/${id}`, data),
  deleteBrand: (id) => apiClient.delete(`/assets/brands/${id}`),
  
  // 设备供应商
  getVendors: (params) => apiClient.get('/assets/vendors', { params }),
  getVendor: (id) => apiClient.get(`/assets/vendors/${id}`),
  createVendor: (data) => apiClient.post('/assets/vendors', data),
  updateVendor: (id, data) => apiClient.put(`/assets/vendors/${id}`, data),
  deleteVendor: (id) => apiClient.delete(`/assets/vendors/${id}`),
  
  // 设备型号
  getModels: (params) => apiClient.get('/assets/models', { params }),
  getModel: (id) => apiClient.get(`/assets/models/${id}`),
  createModel: (data) => apiClient.post('/assets/models', data),
  updateModel: (id, data) => apiClient.put(`/assets/models/${id}`, data),
  deleteModel: (id) => apiClient.delete(`/assets/models/${id}`),
  
  // 机房区域
  getZones: (params) => apiClient.get('/assets/zones', { params }),
  getZone: (id) => apiClient.get(`/assets/zones/${id}`),
  createZone: (data) => apiClient.post('/assets/zones', data),
  updateZone: (id, data) => apiClient.put(`/assets/zones/${id}`, data),
  deleteZone: (id) => apiClient.delete(`/assets/zones/${id}`),
  
  // 机房
  getDataCenters: (params) => apiClient.get('/assets/datacenters', { params }),
  getDataCenter: (id) => apiClient.get(`/assets/datacenters/${id}`),
  createDataCenter: (data) => apiClient.post('/assets/datacenters', data),
  updateDataCenter: (id, data) => apiClient.put(`/assets/datacenters/${id}`, data),
  deleteDataCenter: (id) => apiClient.delete(`/assets/datacenters/${id}`),
  getDataCenter3D: (id) => apiClient.get(`/assets/datacenters/${id}/3d`),
  
  // 机柜
  getCabinets: (params) => apiClient.get('/assets/cabinets', { params }),
  getCabinet: (id) => apiClient.get(`/assets/cabinets/${id}`),
  createCabinet: (data) => apiClient.post('/assets/cabinets', data),
  updateCabinet: (id, data) => apiClient.put(`/assets/cabinets/${id}`, data),
  deleteCabinet: (id) => apiClient.delete(`/assets/cabinets/${id}`),
  getCabinetUtilization: (id) => apiClient.get(`/assets/stats/cabinet-utilization/${id}`),
  
  // 设备扩展
  getDevices: (params) => apiClient.get('/assets/devices', { params }),
  getDevice: (id) => apiClient.get(`/assets/devices/${id}`),
  // 设备列表导出为 Excel（含追溯二维码链接列），responseType 必须是 blob
  exportDevices: (params) => apiClient.get('/assets/devices/export', { params, responseType: 'blob' }),
  getDeviceTypes: () => apiClient.get('/assets/device-types'),
  createDevice: (data) => apiClient.post('/assets/devices', data),
  updateDevice: (id, data) => apiClient.put(`/assets/devices/${id}`, data),
  deleteDevice: (id) => apiClient.delete(`/assets/devices/${id}`),
  clearDeviceComponents: (id) => apiClient.delete(`/device-components/devices/${id}/components`),
  mountDevice: (data) => {
    console.log('[API DEBUG] mountDevice 请求数据:', JSON.stringify(data, null, 2))
    return apiClient.post('/assets/devices/mount', data)
  },
  unmountDevice: (id) => apiClient.post(`/assets/devices/${id}/unmount`),
  getCabinetDevices: (cabinetId) => apiClient.get(`/assets/cabinets/${cabinetId}/devices`),
  // 上架记录
  getMountRecords: (params) => apiClient.get('/assets/mount-records', { params }),
  getMountRecord: (id) => apiClient.get(`/assets/mount-records/${id}`),

  // 统计
  getDeviceStatusStats: () => apiClient.get('/assets/stats/device-status')
}

// 告警通知 API
export const alertNotificationAPI = {
  getConfig: () => apiClient.get('/alert-notification/config'),
  updateConfig: (data) => apiClient.put('/alert-notification/config', data),
  testEmail: (data) => apiClient.post('/alert-notification/test/email', data),
  testWechat: (data) => apiClient.post('/alert-notification/test/wechat', data),
  checkWarranty: () => apiClient.post('/alert-notification/check-warranty')
}

// 系统管理 API
export const systemAPI = {
  getServerTime: () => apiClient.get('/system/time'),
  syncTime: (data) => apiClient.post('/system/time/sync/ntp', data),
  setTime: (data) => apiClient.post('/system/time/set', data),
  getTimeSyncConfig: () => apiClient.get('/system/time/config'),
  saveTimeSyncConfig: (data) => apiClient.post('/system/time/config', data),
  testNtpServer: (data) => apiClient.post('/system/time/ntp/test', data),
  getTimeSyncHistory: (params) => apiClient.get('/system/time/sync/history', { params })
}

// 资产追溯 API
export const assetTraceAPI = {
  // 获取设备追溯信息（通过设备ID）
  getDeviceTraceInfo: (deviceId) => apiClient.get(`/asset-trace/devices/${deviceId}/trace`),
  // 获取设备追溯信息（通过上架编号）
  getDeviceTraceInfoByMountNo: (mountNo) => apiClient.get(`/asset-trace/devices/by-mount-no/${mountNo}/trace`),
  // 获取设备二维码（通过设备ID）
  getDeviceQRCode: (deviceId) => apiClient.get(`/asset-trace/devices/${deviceId}/qrcode`),
  // 获取设备二维码（通过上架编号）
  getDeviceQRCodeByMountNo: (mountNo) => apiClient.get(`/asset-trace/devices/by-mount-no/${mountNo}/qrcode`),
  // 记录上下架操作
  recordMountOperation: (deviceId, data) => apiClient.post(`/asset-trace/devices/${deviceId}/mount-operation`, data),
  // 记录标签打印
  recordLabelPrint: (deviceId, data) => apiClient.post(`/asset-trace/devices/${deviceId}/print-label`, data),
  // 获取上下架历史
  getMountHistory: (deviceId) => apiClient.get(`/asset-trace/devices/${deviceId}/mount-history`),
  // 获取设备连接历史
  getDeviceConnectionHistory: (deviceId) => apiClient.get(`/network-topology/connection-history/${deviceId}`),
  // 获取设备当前活跃连接
  getDeviceActiveConnections: (deviceId) => apiClient.get(`/network-topology/connections?device_id=${deviceId}`)
}

// 触发器告警关联 API
export const triggerAlertAPI = {
  // 获取触发器及其关联的设备信息
  getTriggersWithDevice: (params) => apiClient.get('/trigger-alerts/triggers-with-device', { params }),
  
  // 告警配置管理
  createAlertConfig: (data) => apiClient.post('/trigger-alerts/config', data),
  getAlertConfig: (triggerId) => apiClient.get(`/trigger-alerts/config/${triggerId}`),
  updateAlertConfig: (triggerId, data) => apiClient.put(`/trigger-alerts/config/${triggerId}`, data),
  deleteAlertConfig: (triggerId) => apiClient.delete(`/trigger-alerts/config/${triggerId}`),
  
  // 批量配置
  batchCreateAlertConfig: (triggerIds, data) => apiClient.post('/trigger-alerts/batch-config', triggerIds, { params: data }),
  
  // 告警历史
  getAlertHistory: (params) => apiClient.get('/trigger-alerts/history', { params }),
  getAlertHistoryDetail: (historyId) => apiClient.get(`/trigger-alerts/history/${historyId}`),
  acknowledgeAlert: (historyId, acknowledgedBy) => apiClient.post(`/trigger-alerts/history/${historyId}/acknowledge`, null, { params: { acknowledged_by: acknowledgedBy } }),
  resolveAlert: (historyId) => apiClient.post(`/trigger-alerts/history/${historyId}/resolve`),
  
  // 告警统计
  getAlertStatistics: (params) => apiClient.get('/trigger-alerts/statistics', { params }),
  
  // 通知日志
  getNotificationLogs: (params) => apiClient.get('/trigger-alerts/notification-logs', { params }),
  
  // 测试通知
  testNotification: (triggerId, notificationType) => apiClient.post(`/trigger-alerts/test-notification/${triggerId}`, null, { params: { notification_type: notificationType } }),
  
  // 设备活跃告警
  getDeviceActiveAlerts: (deviceId) => apiClient.get(`/trigger-alerts/devices/${deviceId}/active-alerts`)
}

// 设备通知配置 API
export const deviceNotificationAPI = {
  // 获取设备的通知配置
  getDeviceNotificationConfig: (deviceId) => apiClient.get(`/device-notification/devices/${deviceId}/notification-config`),
  
  // 创建设备的通知配置
  createDeviceNotificationConfig: (deviceId, data) => apiClient.post(`/device-notification/devices/${deviceId}/notification-config`, data),
  
  // 更新设备的通知配置
  updateDeviceNotificationConfig: (deviceId, data) => apiClient.put(`/device-notification/devices/${deviceId}/notification-config`, data),
  
  // 删除设备的通知配置
  deleteDeviceNotificationConfig: (deviceId) => apiClient.delete(`/device-notification/devices/${deviceId}/notification-config`),
  
  // 通知模板管理
  getNotificationTemplates: (params) => apiClient.get('/device-notification/notification-templates', { params }),
  getNotificationTemplate: (templateId) => apiClient.get(`/device-notification/notification-templates/${templateId}`),
  createNotificationTemplate: (data) => apiClient.post('/device-notification/notification-templates', data),
  updateNotificationTemplate: (templateId, data) => apiClient.put(`/device-notification/notification-templates/${templateId}`, data),
  deleteNotificationTemplate: (templateId) => apiClient.delete(`/device-notification/notification-templates/${templateId}`),
  setDefaultTemplate: (templateId) => apiClient.post(`/device-notification/notification-templates/${templateId}/set-default`),
  getTemplateVariables: () => apiClient.get('/device-notification/notification-templates/variables')
}

// 告警通知模板 API (告警管理模块)
export const notificationTemplateAPI = {
  // 获取模板列表
  getTemplates: (params) => apiClient.get('/notification-templates', { params }),
  // 获取单个模板
  getTemplate: (id) => apiClient.get(`/notification-templates/${id}`),
  // 创建模板
  createTemplate: (data) => apiClient.post('/notification-templates', data),
  // 更新模板
  updateTemplate: (id, data) => apiClient.put(`/notification-templates/${id}`, data),
  // 删除模板
  deleteTemplate: (id) => apiClient.delete(`/notification-templates/${id}`),
  // 应用模板到触发器
  applyTemplate: (templateId, triggerId) => apiClient.post(`/notification-templates/${templateId}/apply/${triggerId}`),
  // 初始化默认模板
  initDefaults: () => apiClient.post('/notification-templates/init-defaults'),
  // 初始化示例模板
  initExamples: () => apiClient.post('/notification-templates/init-examples')
}

// 通知配置 API (告警管理-通知配置)
export const notificationConfigAPI = {
  // 获取配置列表
  getConfigs: (params) => apiClient.get('/notification-configs', { params }),
  // 获取单个配置
  getConfig: (id) => apiClient.get(`/notification-configs/${id}`),
  // 创建配置
  createConfig: (data) => apiClient.post('/notification-configs', data),
  // 更新配置
  updateConfig: (id, data) => apiClient.put(`/notification-configs/${id}`, data),
  // 删除配置
  deleteConfig: (id) => apiClient.delete(`/notification-configs/${id}`),
  // 根据类型获取配置
  getConfigsByType: (type) => apiClient.get(`/notification-configs/by-type/${type}`),
  // 测试发送
  testConfig: (id, data) => apiClient.post(`/notification-configs/${id}/test`, data)
}

// 监控模板系统 API
export const monitoringTemplateAPI = {
  // 监控项管理
  getItems: (params) => apiClient.get('/monitoring-template/items', { params }),
  getItem: (id) => apiClient.get(`/monitoring-template/items/${id}`),
  createItem: (data) => apiClient.post('/monitoring-template/items', data),
  updateItem: (id, data) => apiClient.put(`/monitoring-template/items/${id}`, data),
  deleteItem: (id) => apiClient.delete(`/monitoring-template/items/${id}`),
  importItemsFromOIDGroup: (data) => apiClient.post('/monitoring-template/items/import-from-oid-group', data),

  // 应用集管理
  getApplicationSets: (params) => apiClient.get('/monitoring-template/application-sets', { params }),
  getApplicationSet: (id) => apiClient.get(`/monitoring-template/application-sets/${id}`),
  createApplicationSet: (data) => apiClient.post('/monitoring-template/application-sets', data),
  updateApplicationSet: (id, data) => apiClient.put(`/monitoring-template/application-sets/${id}`, data),
  deleteApplicationSet: (id) => apiClient.delete(`/monitoring-template/application-sets/${id}`),

  // 触发器管理
  getTriggers: (params) => apiClient.get('/monitoring-template/triggers', { params }),
  getTrigger: (id) => apiClient.get(`/monitoring-template/triggers/${id}`),
  createTrigger: (data) => apiClient.post('/monitoring-template/triggers', data),
  updateTrigger: (id, data) => apiClient.put(`/monitoring-template/triggers/${id}`, data),
  deleteTrigger: (id) => apiClient.delete(`/monitoring-template/triggers/${id}`),
  bulkImportTriggers: (data) => apiClient.post('/monitoring-template/triggers/bulk-import', data),

  // 图形管理
  getGraphs: (params) => apiClient.get('/monitoring-template/graphs', { params }),
  getGraph: (id) => apiClient.get(`/monitoring-template/graphs/${id}`),
  createGraph: (data) => apiClient.post('/monitoring-template/graphs', data),
  updateGraph: (id, data) => apiClient.put(`/monitoring-template/graphs/${id}`, data),
  deleteGraph: (id) => apiClient.delete(`/monitoring-template/graphs/${id}`),

  // 监控模板管理
  getTemplates: (params) => apiClient.get('/monitoring-template/templates', { params }),
  getTemplate: (id) => apiClient.get(`/monitoring-template/templates/${id}`),
  createTemplate: (data) => apiClient.post('/monitoring-template/templates', data),
  updateTemplate: (id, data) => apiClient.put(`/monitoring-template/templates/${id}`, data),
  deleteTemplate: (id) => apiClient.delete(`/monitoring-template/templates/${id}`),

  // 监控主机管理
  getHosts: (params) => apiClient.get('/monitoring-template/hosts', { params }),
  getHost: (id) => apiClient.get(`/monitoring-template/hosts/${id}`),
  createHost: (data) => apiClient.post('/monitoring-template/hosts', data),
  updateHost: (id, data) => apiClient.put(`/monitoring-template/hosts/${id}`, data),
  deleteHost: (id) => apiClient.delete(`/monitoring-template/hosts/${id}`),
  collectHostData: (id) => apiClient.post(`/monitoring-template/hosts/${id}/collect`),
  testHostSNMP: (id) => apiClient.post(`/monitoring-template/hosts/${id}/test-snmp`),
  getAvailabilityHistory: (params) => apiClient.get('/monitoring-template/hosts/availability-history', { params }),

  // 触发器动作管理
  getTriggerActions: (triggerId) => apiClient.get(`/monitoring-template/triggers/${triggerId}/actions`),
  createTriggerAction: (triggerId, data) => apiClient.post(`/monitoring-template/triggers/${triggerId}/actions`, data),
  updateTriggerAction: (triggerId, actionId, data) => apiClient.put(`/monitoring-template/triggers/${triggerId}/actions/${actionId}`, data),
  deleteTriggerAction: (triggerId, actionId) => apiClient.delete(`/monitoring-template/triggers/${triggerId}/actions/${actionId}`),
  
  // 测试发送
  testAction: (data) => apiClient.post('/monitoring-template/actions/test', data)
}

// 网络拓扑 API
export const networkTopologyAPI = {
  // 获取拓扑设备列表
  // 如果不传deviceType，后端会返回所有已配置同步的设备类型
  getDevices: (deviceType = null) => apiClient.get('/network-topology/devices', { params: deviceType ? { device_type: deviceType } : {} }),
  
  // 获取连接列表
  getConnections: (deviceId) => apiClient.get('/network-topology/connections', { params: { device_id: deviceId } }),
  
  // 创建连接
  createConnection: (data) => apiClient.post('/network-topology/connections', data),
  
  // 更新连接
  updateConnection: (id, data) => apiClient.put(`/network-topology/connections/${id}`, data),
  
  // 删除连接
  deleteConnection: (id) => apiClient.delete(`/network-topology/connections/${id}`),
  
  // 自动发现设备拓扑
  discoverDevice: (deviceId) => apiClient.post(`/network-topology/discover/${deviceId}`),
  
  // 获取可视化数据
  getVisualizationData: (layout = 'hierarchical') => apiClient.get('/network-topology/visualization', { params: { layout } }),
  
  // 获取设备邻居
  getDeviceNeighbors: (deviceId, hop = 1) => apiClient.get(`/network-topology/device/${deviceId}/neighbors`, { params: { hop } }),
  
  // 获取拓扑布局
  getLayout: (name = 'default') => apiClient.get('/network-topology/layout', { params: { name } }),
  
  // 保存拓扑布局
  saveLayout: (data) => apiClient.post('/network-topology/layout', data),
  
  // 更新拓扑布局
  updateLayout: (id, data) => apiClient.put(`/network-topology/layout/${id}`, data),
  
  // 删除拓扑布局
  deleteLayout: (id) => apiClient.delete(`/network-topology/layout/${id}`),
  
  // 获取所有布局列表
  getLayouts: () => apiClient.get('/network-topology/layouts'),
  
  // 获取拓扑图设备类型映射（使用新的同步配置）
  getTopologyTypeMapping: () => apiClient.get('/network-device-sync/topology-types'),

  // 切换设备是否在拓扑图中显示
  toggleDeviceInTopology: (deviceId, inTopology) => apiClient.post(`/network-topology/devices/${deviceId}/toggle-topology`, null, { params: { in_topology: inTopology } }),
  
  // 保存单个节点位置
  saveNodePosition: (deviceId, x, y) => apiClient.post(`/network-topology/devices/${deviceId}/position`, { x, y }),
  
  // 获取单个节点位置
  getNodePosition: (deviceId) => apiClient.get(`/network-topology/devices/${deviceId}/position`),
  
  // 删除单个节点位置
  deleteNodePosition: (deviceId) => apiClient.delete(`/network-topology/devices/${deviceId}/position`),
  
  // 批量保存节点位置
  batchSaveNodePositions: (positions) => apiClient.post('/network-topology/positions', { positions }),
  
  // 获取所有节点位置
  getAllNodePositions: () => apiClient.get('/network-topology/positions'),
  
  // 获取设备端口列表
  getDevicePorts: (deviceId) => apiClient.get(`/network-topology/device/${deviceId}/ports`),

  // 重新生成设备端口
  regenerateDevicePorts: (deviceId) => apiClient.post(`/network-topology/device/${deviceId}/regenerate-ports`),

  // 配线架端口绑定相关API
  // 获取端口绑定详情（用于自动带出对端信息）
  getPortBindingDetail: (deviceId, portId, componentId) => {
    const params = componentId ? { component_id: componentId } : {}
    return apiClient.get(`/patch-panels-v2/port-binding/${deviceId}/${portId}`, { params })
  },

  // 获取配线架端口绑定列表
  getPortBindings: (deviceId, portId) => apiClient.get('/patch-panels-v2/port-bindings', { params: { device_id: deviceId, port_id: portId } })
}

// 网络设备类型同步配置 API
export const networkDeviceSyncAPI = {
  // 获取同步配置列表
  getSyncConfigs: (params) => apiClient.get('/network-device-sync/', { params }),
  
  // 获取已同步的类型代码
  getSyncedTypeCodes: () => apiClient.get('/network-device-sync/synced-types'),
  
  // 获取拓扑图类型
  getTopologyTypes: () => apiClient.get('/network-device-sync/topology-types'),
  
  // 创建同步配置
  createSyncConfig: (data) => apiClient.post('/network-device-sync/', data),
  
  // 更新同步配置
  updateSyncConfig: (id, data) => apiClient.put(`/network-device-sync/${id}`, data),
  
  // 删除同步配置
  deleteSyncConfig: (id) => apiClient.delete(`/network-device-sync/${id}`),
  
  // 切换同步状态
  toggleSync: (id) => apiClient.post(`/network-device-sync/toggle-sync/${id}`),
  
  // 切换拓扑显示
  toggleTopology: (id) => apiClient.post(`/network-device-sync/toggle-topology/${id}`),
  
  // 初始化默认配置
  initDefaults: () => apiClient.post('/network-device-sync/init-defaults'),
  
  // 获取可用类型
  getAvailableTypes: () => apiClient.get('/network-device-sync/available-types')
}

// 设备类型管理 API（保留用于兼容）
export const deviceTypeAPI = {
  // 获取设备类型列表
  getDeviceTypes: (params) => apiClient.get('/device-types/', { params }),
  
  // 获取设备类型详情
  getDeviceType: (id) => apiClient.get(`/device-types/${id}`),
  
  // 创建设备类型
  createDeviceType: (data) => apiClient.post('/device-types/', data),
  
  // 更新设备类型
  updateDeviceType: (id, data) => apiClient.put(`/device-types/${id}`, data),
  
  // 删除设备类型
  deleteDeviceType: (id) => apiClient.delete(`/device-types/${id}`),
  
  // 根据代码获取设备类型
  getDeviceTypeByCode: (code) => apiClient.get(`/device-types/by-code/${code}`),
  
  // 初始化默认设备类型
  initDefaultTypes: () => apiClient.post('/device-types/init-defaults'),
  
  // 获取拓扑图设备类型映射
  getTopologyMapping: () => apiClient.get('/device-types/topology/mapping')
}

// 设备组件 API
export const deviceAPI = {
  // 获取设备组件列表
  getDeviceComponents: (deviceId) => apiClient.get(`/device-components/devices/${deviceId}/components`),
  // 释放单个端口/组件
  releaseComponent: (componentId) => apiClient.delete(`/device-components/components/${componentId}`)
}

// 服务器管理 API
export const serverManagementAPI = {
  // vCenter管理
  getVCenters: () => apiClient.get('/server-management/vcenters'),
  getVCenter: (id) => apiClient.get(`/server-management/vcenters/${id}`),
  createVCenter: (data) => apiClient.post('/server-management/vcenters', data),
  updateVCenter: (id, data) => apiClient.put(`/server-management/vcenters/${id}`, data),
  deleteVCenter: (id) => apiClient.delete(`/server-management/vcenters/${id}`),
  testVCenterConnection: (id) => apiClient.post(`/server-management/vcenters/${id}/test`),
  syncVCenter: (id) => apiClient.post(`/server-management/vcenters/${id}/sync`),

  // 服务器管理
  getServers: (params) => apiClient.get('/server-management/servers', { params }),
  getServer: (id) => apiClient.get(`/server-management/servers/${id}`),
  createServer: (data) => apiClient.post('/server-management/servers', data),
  updateServer: (id, data) => apiClient.put(`/server-management/servers/${id}`, data),
  deleteServer: (id) => apiClient.delete(`/server-management/servers/${id}`),

  // 虚拟机性能监控
  getServerMetrics: (id) => apiClient.get(`/server-management/servers/${id}/metrics`),
  getServerMetricsHistory: (id, metricName, hours = 24) =>
    apiClient.get(`/server-management/servers/${id}/metrics/history`, { params: { metric_name: metricName, hours } }),
  collectVCenterMetrics: (id) => apiClient.post(`/server-management/vcenters/${id}/collect-metrics`),
  getVCenterMetricsConfig: (id) => apiClient.get(`/server-management/vcenters/${id}/metrics-config`),
  updateVCenterMetricsConfig: (id, data) => apiClient.put(`/server-management/vcenters/${id}/metrics-config`, data),

  // 仪表板统计
  getDashboardStats: () => apiClient.get('/server-management/dashboard/stats')
}

// 认证 API
export const authAPI = {
  // 登录
  login: (data) => apiClient.post('/auth/login', data),
  // 登出
  logout: () => apiClient.post('/auth/logout'),
  // 刷新Token
  refreshToken: (refreshToken) => apiClient.post('/auth/refresh', { refresh_token: refreshToken }),
  // 获取当前用户信息
  getMe: () => apiClient.get('/auth/me'),
  // 修改密码
  changePassword: (data) => apiClient.post('/auth/change-password', data)
}

// 系统管理 API - 用户管理
export const systemManagementAPI = {
  // 用户管理
  getUsers: (params) => apiClient.get('/system-management/users', { params }),
  getUser: (id) => apiClient.get(`/system-management/users/${id}`),
  createUser: (data) => apiClient.post('/system-management/users', data),
  updateUser: (id, data) => apiClient.put(`/system-management/users/${id}`, data),
  deleteUser: (id) => apiClient.delete(`/system-management/users/${id}`),
  resetPassword: (id, data) => apiClient.post(`/system-management/users/${id}/reset-password`, data),
  updateProfile: (data) => apiClient.put('/system-management/users/profile', data),

  // 角色管理
  getRoles: (params) => apiClient.get('/system-management/roles', { params }),
  getRole: (id) => apiClient.get(`/system-management/roles/${id}`),
  createRole: (data) => apiClient.post('/system-management/roles', data),
  updateRole: (id, data) => apiClient.put(`/system-management/roles/${id}`, data),
  deleteRole: (id) => apiClient.delete(`/system-management/roles/${id}`),
  assignRolePermissions: (id, data) => apiClient.post(`/system-management/roles/${id}/permissions`, data),
  getRolePermissions: (id) => apiClient.get(`/system-management/roles/${id}/permissions`),

  // 权限管理
  getPermissions: (params) => apiClient.get('/system-management/permissions', { params }),
  getPermission: (id) => apiClient.get(`/system-management/permissions/${id}`),
  createPermission: (data) => apiClient.post('/system-management/permissions', data),
  updatePermission: (id, data) => apiClient.put(`/system-management/permissions/${id}`, data),
  deletePermission: (id) => apiClient.delete(`/system-management/permissions/${id}`),
  getPermissionTree: () => apiClient.get('/system-management/permissions/tree'),
  movePermission: (id, data) => apiClient.post(`/system-management/permissions/${id}/move`, data),
  sortPermissions: (data) => apiClient.post('/system-management/permissions/sort', data),
  getUserMenus: () => apiClient.get('/system-management/menus'),

  // 部门管理
  getDepartments: () => apiClient.get('/system-management/departments'),
  getDepartment: (id) => apiClient.get(`/system-management/departments/${id}`),
  createDepartment: (data) => apiClient.post('/system-management/departments', data),
  updateDepartment: (id, data) => apiClient.put(`/system-management/departments/${id}`, data),
  deleteDepartment: (id) => apiClient.delete(`/system-management/departments/${id}`),
  getDepartmentTree: () => apiClient.get('/system-management/departments/tree'),

  // 岗位管理
  getPositions: (params) => apiClient.get('/system-management/positions', { params }),
  getPosition: (id) => apiClient.get(`/system-management/positions/${id}`),
  createPosition: (data) => apiClient.post('/system-management/positions', data),
  updatePosition: (id, data) => apiClient.put(`/system-management/positions/${id}`, data),
  deletePosition: (id) => apiClient.delete(`/system-management/positions/${id}`),

  // 登录日志
  getLoginLogs: (params) => apiClient.get('/system-management/login-logs', { params }),

  // 操作日志
  getOperationLogs: (params) => apiClient.get('/system-management/operation-logs', { params })
}

// 日志服务器 API
export const logServerAPI = {
  getLogs: (params) => apiClient.get('/log-server/logs', { params }),
  getLog: (logId) => apiClient.get(`/log-server/logs/${logId}`),
  deleteLog: (logId) => apiClient.delete(`/log-server/logs/${logId}`),
  batchDeleteLogs: (ids) => apiClient.post('/log-server/logs/batch-delete', { ids }),
  cleanLogs: (data) => apiClient.post('/log-server/logs/clean', data),
  importLogs: (data) => apiClient.post('/log-server/logs/import', data),
  getRules: (params) => apiClient.get('/log-server/rules', { params }),
  createRule: (data) => apiClient.post('/log-server/rules', data),
  updateRule: (ruleId, data) => apiClient.put(`/log-server/rules/${ruleId}`, data),
  deleteRule: (ruleId) => apiClient.delete(`/log-server/rules/${ruleId}`),
  getConfig: () => apiClient.get('/log-server/config'),
  updateConfig: (data) => apiClient.put('/log-server/config', data),
  getStats: (params) => apiClient.get('/log-server/stats', { params })
}

export const recoveryEventsAPI = {
  getEvents: (params) => apiClient.get('/recovery-events', { params }),
  getEvent: (id) => apiClient.get(`/recovery-events/${id}`),
  deleteEvent: (id) => apiClient.delete(`/recovery-events/${id}`),
  getStats: (params) => apiClient.get('/recovery-events/stats/summary', { params })
}

export const recoveryNotificationPolicyAPI = {
  getPolicies: (params) => apiClient.get('/recovery-notification-policies', { params }),
  getPolicy: (id) => apiClient.get(`/recovery-notification-policies/${id}`),
  createPolicy: (data) => apiClient.post('/recovery-notification-policies', data),
  updatePolicy: (id, data) => apiClient.put(`/recovery-notification-policies/${id}`, data),
  deletePolicy: (id) => apiClient.delete(`/recovery-notification-policies/${id}`),
  matchPolicy: (params) => apiClient.get('/recovery-notification-policies/match/best', { params })
}

export const api = {
  logServerAPI,
  recoveryEventsAPI
}

export default apiClient